import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { contractsAPI } from '../services/api';
import { useAuth } from '../auth/AuthContext';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft, FileSignature, Pencil, ChevronRight, FileText,
  DollarSign, Loader2, AlertTriangle, History, Plus, XCircle,
  LayoutDashboard, Tag, Clock, Send
} from 'lucide-react';
import { toast } from 'sonner';

const CURRENCY_SYMBOLS = { GBP: '\u00a3', EUR: '\u20ac', USD: '$', CAD: 'CA$', AUD: 'A$' };
const fmt = (n, code) =>
  `${CURRENCY_SYMBOLS[code] || ''}${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const fmtDateShort = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

const PRODUCT_LABELS = { STANDARD: 'C4P Standard', ADVANCED: 'C4P Advanced', MTPRO: 'C4P MTPro' };

const InfoRow = ({ label, value }) => (
  <div className="flex items-baseline gap-3 py-1.5" style={{ borderBottom: '1px solid hsl(var(--border) / 0.5)' }}>
    <span className="text-xs font-semibold uppercase tracking-wide flex-shrink-0 w-28"
      style={{ color: 'hsl(var(--muted-fg))' }}>{label}</span>
    <span className="text-sm font-medium" style={{ color: 'hsl(var(--fg))' }}>{value || '-'}</span>
  </div>
);

const ContractStatusBadge = ({ status }) => {
  if (status === 'LIVE') {
    return <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-50 text-green-800 border border-green-200">LIVE</span>;
  }
  if (status === 'TERMINATED') {
    return <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-800 border border-red-200">TERMINATED</span>;
  }
  return <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">{status}</span>;
};

const HistoryEntry = ({ entry, isFirst, currency }) => (
  <div
    className="flex items-center justify-between p-3 rounded-lg"
    style={{ background: isFirst ? 'hsl(var(--muted) / 0.4)' : 'hsl(var(--muted) / 0.15)' }}
    data-testid={`contract-history-entry-${entry.quote_id}`}
  >
    <div className="flex items-center gap-3">
      {isFirst && (
        <span className="text-xs px-1.5 py-0.5 rounded font-semibold"
          style={{ background: '#f0fdf4', color: '#14532d', border: '1px solid #bbf7d0' }}>
          Current
        </span>
      )}
      <div>
        <span className="font-mono text-xs font-semibold" style={{ color: 'hsl(var(--c4p-navy-900))' }}>
          {entry.quote_number}
        </span>
        <div className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>
          Signed {fmtDateShort(entry.signed_at)}
        </div>
      </div>
    </div>
    <div className="flex items-center gap-3">
      <span className="text-sm font-semibold" style={{ color: 'hsl(var(--c4p-navy-900))' }}>
        {fmt(entry.monthly_total, currency)}/mo
      </span>
      <Link to={`/quotes/${entry.quote_id}`}
        data-testid={`contract-history-quote-link-${entry.quote_id}`}
        onClick={e => e.stopPropagation()}>
        <Button variant="ghost" size="sm" className="text-xs gap-1">
          View <ChevronRight size={11} />
        </Button>
      </Link>
    </div>
  </div>
);

const STATUS_BADGE = {
  DRAFT: { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0', label: 'Draft', icon: Clock },
  SENT:  { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe', label: 'Sent — Awaiting Signature', icon: Send },
};

const PendingCREntry = ({ quote }) => {
  const s = STATUS_BADGE[quote.status] || STATUS_BADGE.DRAFT;
  const Icon = s.icon;
  return (
    <div className="flex items-center justify-between p-3 rounded-lg border-2"
      style={{ background: s.bg, borderColor: s.border }}
      data-testid={`contract-pending-cr-${quote.id}`}>
      <div className="flex items-center gap-3">
        <span className="flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded"
          style={{ background: 'white', color: s.color, border: `1px solid ${s.border}` }}>
          <Icon size={10} /> {s.label}
        </span>
        <div>
          <span className="font-mono text-xs font-semibold" style={{ color: 'hsl(var(--c4p-navy-900))' }}>
            {quote.quote_number}
          </span>
          <div className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>
            Change Request · Created {fmtDateShort(quote.created_at)}
          </div>
        </div>
      </div>
      <Link to={`/quotes/${quote.id}`} onClick={e => e.stopPropagation()}
        data-testid={`contract-pending-cr-link-${quote.id}`}>
        <Button variant="ghost" size="sm" className="text-xs gap-1">
          View <ChevronRight size={11} />
        </Button>
      </Link>
    </div>
  );
};

const NotFoundState = ({ onBack }) => (
  <div className="flex items-center justify-center h-full p-6">
    <div className="text-center">
      <AlertTriangle size={32} className="mx-auto mb-3 opacity-40" style={{ color: '#ef4444' }} />
      <p className="font-semibold">Contract not found or access denied.</p>
      <Button variant="outline" className="mt-4" onClick={onBack}>
        <ArrowLeft size={14} className="mr-2" /> Back to Contracts
      </Button>
    </div>
  </div>
);

const SectionPanel = ({ icon: Icon, title, children, headerRight }) => (
  <div className="rounded-xl border bg-white p-5" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <Icon size={14} style={{ color: 'hsl(var(--c4p-navy-900))' }} />
        <span className="text-xs font-bold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))' }}>
          {title}
        </span>
      </div>
      {headerRight}
    </div>
    {children}
  </div>
);

const TerminateBadge = () => (
  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-800 border border-red-200">
    TERMINATED
  </span>
);

// Metadata display helpers used across contract detail sections
const MetaLabel = ({ children }) => (
  <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))' }}>
    {children}
  </span>
);
const MetaValue = ({ children }) => (
  <span className="text-sm font-medium" style={{ color: 'hsl(var(--fg))' }}>
    {children || '-'}
  </span>
);

// ── Contract Action Panel — buttons rendered in the page header ──────────────
// Defined outside export default to keep line numbers within compiler limits
const ContractActionButtons = ({
  isLive, canManage, creatingCR, onCreateCR, onArchive, onTerminate
}) => (
  <>
    {isLive && (
      <Button size="sm" onClick={onCreateCR} disabled={creatingCR}
        data-testid="contract-create-change-request-btn"
        style={{ background: 'hsl(var(--c4p-navy-900))', color: 'white' }}>
        {creatingCR ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Plus size={13} className="mr-1" />}
        Create Change Request
      </Button>
    )}
    {canManage && isLive && (
      <Button variant="outline" size="sm" onClick={onArchive}
        data-testid="contract-archive-btn"
        style={{ borderColor: '#fca5a5', color: '#b91c1c' }}>
        Archive
      </Button>
    )}
    {canManage && isLive && (
      <Button variant="outline" size="sm" onClick={onTerminate}
        data-testid="contract-terminate-btn"
        style={{ borderColor: '#ef4444', color: '#7f1d1d', background: '#fff1f2' }}>
        <XCircle size={13} className="mr-1" /> Terminate
      </Button>
    )}
  </>
);

// ── Custom hook: all contract detail state and actions ───────────────────────
function useContractDetail(id, navigate) {
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [showRename, setShowRename] = useState(false);
  const [renameName, setRenameName] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [showTerminate, setShowTerminate] = useState(false);
  const [terminating, setTerminating] = useState(false);
  const [terminateReason, setTerminateReason] = useState('');
  const [creatingCR, setCreatingCR] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await contractsAPI.get(id);
      setContract(data);
      setRenameName(data.contract_name || '');
    } catch {
      setErr('Contract not found or access denied.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const doRename = useCallback(async (name) => {
    if (name && name.trim()) {
      setRenaming(true);
      try {
        const { data } = await contractsAPI.rename(id, name.trim());
        setContract(prev => ({ ...prev, contract_name: data.contract_name }));
        setShowRename(false);
        toast.success('Contract renamed successfully');
      } catch {
        toast.error('Failed to rename contract');
      } finally {
        setRenaming(false);
      }
    }
  }, [id]);

  const doCreateChangeRequest = async () => {
    setCreatingCR(true);
    try {
      const { data } = await contractsAPI.createChangeRequest(id);
      toast.success(`Change request created: ${data.quote_number}`);
      navigate(`/quotes/${data.quote_id}/edit`);
    } catch (err2) {
      toast.error((err2 && err2.response && err2.response.data && err2.response.data.detail) || 'Failed to create change request');
    } finally {
      setCreatingCR(false);
    }
  };

  const doArchive = async () => {
    setArchiving(true);
    try {
      await contractsAPI.archive(id);
      setContract(prev => ({ ...prev, status: 'ARCHIVED' }));
      setShowArchive(false);
      toast.success('Contract archived');
    } catch (err2) {
      toast.error((err2 && err2.response && err2.response.data && err2.response.data.detail) || 'Failed to archive contract');
    } finally {
      setArchiving(false);
    }
  };

  const doTerminate = async () => {
    setTerminating(true);
    try {
      await contractsAPI.terminate(id, terminateReason.trim() || null);
      setContract(prev => ({ ...prev, status: 'TERMINATED', terminated_at: new Date().toISOString() }));
      setShowTerminate(false);
      setTerminateReason('');
      toast.success('Contract terminated');
    } catch (err2) {
      toast.error((err2 && err2.response && err2.response.data && err2.response.data.detail) || 'Failed to terminate contract');
    } finally {
      setTerminating(false);
    }
  };

  return {
    contract, loading, err,
    showRename, setShowRename, renameName, setRenameName, renaming,
    showArchive, setShowArchive, archiving,
    showTerminate, setShowTerminate, terminating, terminateReason, setTerminateReason,
    creatingCR, load,
    doRename, doCreateChangeRequest, doArchive, doTerminate,
  };
}

export default function ContractDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isManager } = useAuth();
  const canManage = isAdmin; // Super Admin only — archive and terminate are irreversible
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';
  const setTab = (t) => setSearchParams(t === 'overview' ? {} : { tab: t }, { replace: true });

  const {
    contract, loading, err,
    showRename, setShowRename, renameName, setRenameName, renaming,
    showArchive, setShowArchive, archiving,
    showTerminate, setShowTerminate, terminating, terminateReason, setTerminateReason,
    creatingCR,
    doRename, doCreateChangeRequest, doArchive, doTerminate,
  } = useContractDetail(id, navigate);

  if (loading) return (
    <div className="p-6 space-y-4">
      <Skeleton className="h-10 w-64" />
      <Skeleton className="h-12 w-96" />
      <Skeleton className="h-64 w-full" />
    </div>
  );

  if (err) return <NotFoundState onBack={() => navigate('/contracts')} />;

  const isLive = contract.status === 'LIVE';
  const isManualContract = contract.contract_source === 'manual';
  const productLabel = (() => {
    const cat = contract.quote_category || 'PRODUCTION';
    const pc = contract.product_code || '';
    if (cat === 'TEST_SYSTEM') return 'C4P Custom Test System';
    if (cat === 'BESPOKE') return 'C4P Custom';
    if (cat === 'MANUAL') return 'C4P Custom';  // Manual pricing, same product family
    return PRODUCT_LABELS[pc] || pc;
  })();

  const historyReversed = [...(contract.quote_history || [])].reverse();
  const pendingQuotes = contract.pending_quotes || [];
  const snap = contract.current_quote?.pricing_snapshot || {};
  const lineItems = snap.line_items || [];
  const sym = CURRENCY_SYMBOLS[contract.currency] || '';
  const applyVat = contract.currency === 'GBP';

  const TABS = [
    { id: 'overview', label: 'Overview',   icon: LayoutDashboard },
    { id: 'pricing',  label: 'Pricing',    icon: Tag },
    { id: 'history',  label: `History (${(contract.quote_history || []).length + (contract.pending_quotes || []).length})`, icon: History },
  ];

  return (
    <div className="flex flex-col min-h-full" style={{ background: '#f8fafc' }}>
      {/* ── Hero Header ── */}
      <div className="bg-white border-b px-8 py-6 flex-shrink-0"
        style={{ borderColor: '#e2e8f0' }}>
        {/* Breadcrumb row */}
        <button onClick={() => navigate('/contracts')}
          className="flex items-center gap-1.5 text-xs font-medium mb-4 hover:opacity-70 transition-opacity"
          style={{ color: 'hsl(var(--muted-fg))' }}
          data-testid="contract-back-btn">
          <ArrowLeft size={13} /> Contracts
        </button>
        {/* Main header row */}
        <div className="flex items-start justify-between gap-6 flex-wrap">
          {/* Left: identity */}
          <div className="flex flex-col gap-2 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-mono text-xl font-bold" style={{ fontFamily: 'var(--font-display)', color: 'hsl(var(--c4p-navy-900))' }}
                data-testid="contract-header-title">
                {contract.contract_number}
              </span>
              <ContractStatusBadge status={contract.status} data-testid="contract-status-badge" />
              {isManualContract && (
                <span className="text-xs font-medium px-1.5 py-0.5 rounded"
                  style={{ background: '#fff7ed', color: '#9a3412', border: '1px solid #fdba74' }}>
                  Manual Pricing
                </span>
              )}
            </div>
            <p className="text-2xl font-bold leading-tight"
              style={{ fontFamily: 'var(--font-display)', color: 'hsl(var(--c4p-navy-900))' }}>
              {contract.contract_name}
            </p>
            <div className="flex items-center gap-2 flex-wrap text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
              {contract.customer_name && <span>{contract.customer_name}</span>}
              {contract.tenant_name && <><span>·</span><span>{contract.tenant_name}</span></>}
              {contract.region_name && <><span>·</span><span>{contract.region_name}</span></>}
            </div>
          </div>
          {/* Right: monthly total + actions */}
          <div className="flex flex-col items-end gap-3 flex-shrink-0">
            {/* Monthly total */}
            {contract.monthly_total > 0 && (
              <div className="text-right">
                <p className="text-xs font-semibold uppercase tracking-widest mb-0.5"
                  style={{ color: 'hsl(var(--muted-fg))' }}>Monthly Total</p>
                <p className="text-4xl font-light tabular-nums leading-none"
                  style={{ fontFamily: 'var(--font-display)', color: 'hsl(var(--c4p-navy-900))' }}
                  data-testid="contract-monthly-total">
                  {fmt(contract.monthly_total, contract.currency)}
                </p>
                {applyVat && (
                  <p className="text-sm mt-0.5 tabular-nums" style={{ color: '#d97706' }}>
                    {sym}{(contract.monthly_total * 1.2).toFixed(2)} inc. VAT
                  </p>
                )}
              </div>
            )}
            {/* Action buttons */}
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <Button variant="outline" size="sm"
                onClick={() => { setRenameName(contract.contract_name || ''); setShowRename(true); }}
                data-testid="action-rename-contract">
                <Pencil size={13} className="mr-1" /> Rename
              </Button>
              {isLive && (
                <Button size="sm" onClick={doCreateChangeRequest} disabled={creatingCR}
                  data-testid="action-create-cr"
                  style={{ background: 'hsl(var(--c4p-orange-600))', color: 'white' }}
                  className="hover:opacity-90 transition-opacity">
                  {creatingCR ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Plus size={13} className="mr-1" />}
                  Create Change Request
                </Button>
              )}
              {canManage && isLive && (
                <Button variant="outline" size="sm" onClick={() => setShowArchive(true)}
                  data-testid="action-archive-contract"
                  style={{ borderColor: '#fca5a5', color: '#b91c1c' }}>
                  Archive
                </Button>
              )}
              {canManage && isLive && (
                <Button variant="outline" size="sm" onClick={() => setShowTerminate(true)}
                  data-testid="action-terminate-contract"
                  style={{ borderColor: '#ef4444', color: '#7f1d1d', background: '#fff1f2' }}>
                  <XCircle size={13} className="mr-1" /> Terminate
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div className="px-8 flex gap-0.5 flex-shrink-0 bg-white"
        style={{ borderBottom: '1px solid #e2e8f0' }}>
        {TABS.map(({ id: tid, label, icon: Icon }) => (
          <button key={tid} onClick={() => setTab(tid)}
            data-testid={`tab-${tid}`}
            className="flex items-center gap-1.5 px-4 py-3 text-sm font-medium transition-colors outline-none"
            style={{
              color: activeTab === tid ? 'hsl(var(--c4p-navy-900))' : 'hsl(var(--muted-fg))',
              borderBottom: activeTab === tid ? '2px solid hsl(var(--c4p-navy-900))' : '2px solid transparent',
              marginBottom: '-1px',
              background: 'transparent',
            }}>
            <Icon size={13} />
            {label}
          </button>
        ))}
      </div>

      {/* ── Tab content ── */}
      <div className="flex-1 overflow-auto px-8 py-8 w-full">

        {/* ══ OVERVIEW TAB ══ */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Bento grid: 12-col — Commercial (8-col left) + Contract Details (4-col right) */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">

              {/* Commercial — 8 columns */}
              <div className="md:col-span-8 bg-white rounded-xl border overflow-hidden"
                style={{ borderColor: '#e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
                data-testid="card-commercial-details">
                <div className="px-6 py-4 flex items-center gap-2"
                  style={{ borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
                  <DollarSign size={14} style={{ color: 'hsl(var(--muted-fg))' }} />
                  <p className="text-xs font-bold uppercase tracking-widest" style={{ color: 'hsl(var(--muted-fg))' }}>Commercial</p>
                </div>
                <div className="p-6">
                  {/* Key metric prominent */}
                  <div className="pb-5 mb-5" style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: 'hsl(var(--muted-fg))' }}>Monthly Total</p>
                    <p className="text-5xl font-light tabular-nums" style={{ fontFamily: 'var(--font-display)', color: 'hsl(var(--c4p-navy-900))' }}>
                      {fmt(contract.monthly_total, contract.currency)}
                    </p>
                    {applyVat && contract.monthly_total > 0 && (
                      <p className="text-sm mt-1 tabular-nums" style={{ color: '#d97706' }}>
                        {sym}{(contract.monthly_total * 1.2).toFixed(2)} per month including VAT
                      </p>
                    )}
                  </div>
                  {/* Metrics grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { label: 'Currency', value: contract.currency },
                      { label: 'Users', value: contract.num_users != null ? `${contract.num_users}` : '—' },
                      { label: 'Term', value: contract.term_months ? `${contract.term_months} months` : '—' },
                      { label: 'Billing', value: contract.billing_frequency || contract.billing_model || '—' },
                    ].map(({ label, value }) => (
                      <div key={label} className="p-3 rounded-lg" style={{ background: '#f8fafc', border: '1px solid #f1f5f9' }}>
                        <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: 'hsl(var(--muted-fg))' }}>{label}</p>
                        <p className="text-sm font-bold" style={{ color: 'hsl(var(--c4p-navy-900))' }}>{value}</p>
                      </div>
                    ))}
                  </div>
                  {snap.user_tier_label && (
                    <p className="mt-3 text-xs font-medium" style={{ color: '#7c3aed' }}>Tier: {snap.user_tier_label}</p>
                  )}
                </div>
              </div>

              {/* Contract Details — 4 columns */}
              <div className="md:col-span-4 bg-white rounded-xl border overflow-hidden"
                style={{ borderColor: '#e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
                data-testid="card-contract-details">
                <div className="px-6 py-4 flex items-center gap-2"
                  style={{ borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
                  <FileSignature size={14} style={{ color: 'hsl(var(--muted-fg))' }} />
                  <p className="text-xs font-bold uppercase tracking-widest" style={{ color: 'hsl(var(--muted-fg))' }}>Contract Details</p>
                </div>
                <div className="px-6 py-4 space-y-0">
                  {[
                    { label: 'Product', value: <span className="flex items-center gap-1.5 flex-wrap">{productLabel}{isManualContract && <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: '#fff7ed', color: '#9a3412', border: '1px solid #fdba74' }}>Manual Pricing</span>}</span> },
                    { label: 'Customer', value: contract.customer_name },
                    contract.tenant_name ? { label: 'Tenant', value: contract.tenant_name } : null,
                    { label: 'PSM', value: contract.psm_user_name },
                    { label: 'Billing', value: contract.billing_model },
                    { label: 'Region', value: contract.region_name },
                    contract.contract_start_date ? { label: 'Start Date', value: new Date(contract.contract_start_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) } : null,
                    contract.payment_method ? { label: 'Payment', value: contract.payment_method } : null,
                    contract.po_number ? { label: 'PO Number', value: contract.po_number } : null,
                  ].filter(Boolean).map(({ label, value }) => (
                    <div key={label} className="flex items-baseline justify-between py-2.5"
                      style={{ borderBottom: '1px solid #f8fafc' }}>
                      <span className="text-xs font-semibold uppercase tracking-widest flex-shrink-0 w-24"
                        style={{ color: 'hsl(var(--muted-fg))' }}>{label}</span>
                      <span className="text-sm font-medium text-right ml-2" style={{ color: 'hsl(var(--c4p-navy-900))' }}>{value || '—'}</span>
                    </div>
                  ))}
                </div>
                {/* Billing contact & signing contact */}
                {(contract.billing_contact || contract.signing_contact) && (
                  <div className="px-6 pb-4 space-y-3" style={{ borderTop: '1px solid #f1f5f9', paddingTop: '12px' }}>
                    {contract.billing_contact && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: 'hsl(var(--muted-fg))' }}>Billing Contact</p>
                        <p className="text-sm font-semibold" style={{ color: 'hsl(var(--c4p-navy-900))' }}>{contract.billing_contact.name}{contract.billing_contact.title ? `, ${contract.billing_contact.title}` : ''}</p>
                        {contract.billing_contact.email && <p className="text-xs" style={{ color: '#1d4ed8' }}>{contract.billing_contact.email}</p>}
                        {contract.billing_contact.phone && <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{contract.billing_contact.phone}</p>}
                      </div>
                    )}
                    {contract.signing_contact && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: 'hsl(var(--muted-fg))' }}>Order Placed By</p>
                        <p className="text-sm font-semibold" style={{ color: 'hsl(var(--c4p-navy-900))' }}>{contract.signing_contact.name}</p>
                        {contract.signing_contact.email && <p className="text-xs" style={{ color: '#1d4ed8' }}>{contract.signing_contact.email}</p>}
                        {contract.signing_contact.signed_at && <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{new Date(contract.signing_contact.signed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>}
                      </div>
                    )}
                  </div>
                )}
                {/* Metadata at bottom */}
                <div className="px-6 py-3 text-xs space-y-0.5" style={{ borderTop: '1px solid #f1f5f9', background: '#f8fafc', color: 'hsl(var(--muted-fg))' }}>
                  <div>Created: {fmtDate(contract.created_at)}</div>
                  <div>Updated: {fmtDate(contract.updated_at)}</div>
                  {contract.terminated_at && <div style={{ color: '#dc2626' }}>Terminated: {fmtDate(contract.terminated_at)}</div>}
                </div>
              </div>
            </div>

            {/* Current Quote — full width */}
            {contract.current_quote_id && (
              <div className="bg-white rounded-xl border overflow-hidden"
                style={{ borderColor: '#e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
                data-testid="card-current-quote">
                <div className="px-6 py-4 flex items-center justify-between"
                  style={{ borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
                  <div className="flex items-center gap-2">
                    <FileText size={14} style={{ color: 'hsl(var(--muted-fg))' }} />
                    <p className="text-xs font-bold uppercase tracking-widest" style={{ color: 'hsl(var(--muted-fg))' }}>Current Quote</p>
                  </div>
                  <Link to={`/quotes/${contract.current_quote_id}`} data-testid="contract-view-current-quote-link">
                    <Button variant="outline" size="sm" className="text-xs gap-1">View Quote <ChevronRight size={12} /></Button>
                  </Link>
                </div>
                <div className="px-6 py-4">
                  {contract.current_quote && (
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-sm font-bold" style={{ color: 'hsl(var(--c4p-navy-900))' }}>
                        {contract.current_quote.quote_number}
                      </span>
                      {contract.current_quote.quote_name && (
                        <span className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
                          {contract.current_quote.quote_name}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ PRICING TAB ══ */}
        {activeTab === 'pricing' && (
          <div className="space-y-4">
            {lineItems.length === 0 ? (
              <div className="rounded-xl border bg-white p-10 text-center" style={{ borderColor: '#e2e8f0' }}>
                <Tag size={28} className="mx-auto mb-3" style={{ color: 'hsl(var(--border))' }} />
                <p className="text-sm font-medium" style={{ color: 'hsl(var(--c4p-navy-900))' }}>No pricing data available</p>
                <p className="text-xs mt-1" style={{ color: 'hsl(var(--muted-fg))' }}>
                  <Link to={`/quotes/${contract.current_quote_id}`} className="underline">View the quote</Link> for full pricing details.
                </p>
              </div>
            ) : (
              <div className="rounded-xl border bg-white overflow-hidden" style={{ borderColor: '#e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid hsl(var(--border))', background: 'hsl(var(--muted))' }}>
                  <p className="text-xs font-bold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))' }}>
                    Monthly Subscription Pricing — {contract.currency}
                  </p>
                  {contract.current_quote_id && (
                    <Link to={`/quotes/${contract.current_quote_id}`}>
                      <Button variant="outline" size="sm" className="text-xs gap-1">View Full Quote <ChevronRight size={11} /></Button>
                    </Link>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: 'hsl(var(--c4p-navy-900))', color: 'white' }}>
                        <th className="text-center px-4 py-2 text-xs font-bold w-12">Qty</th>
                        <th className="text-left px-4 py-2 text-xs font-bold">Description</th>
                        <th className="text-right px-4 py-2 text-xs font-bold">Unit Price</th>
                        <th className="text-right px-4 py-2 text-xs font-bold">Monthly</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineItems.map((l, i) => {
                        // Tenant Direct: show the saved bespoke tenant price if set, otherwise
                        // gross up PUPM and non-user lines via the standard margin formula —
                        // same logic as QuoteDetailPage so totals always reconcile.
                        const isTD = contract.billing_model === 'TENANT_DIRECT';
                        const hasBespoke = isTD && l.tenant_unit_price != null;
                        const tdBase = isTD && !hasBespoke && l.category === 'base';
                        const tdNonUser = isTD && !hasBespoke && !tdBase && !l.is_included;
                        const tenantPupm = tdBase ? (l.unit_price || 0) + (snap.partner_margin_pupm || 0) : null;
                        const nonUserFactor = tdNonUser ? 1 + ((snap.partner_margin_percent || 0) / 100) : 1;
                        const displayUnitPrice = hasBespoke ? l.tenant_unit_price
                          : tenantPupm !== null ? tenantPupm
                          : tdNonUser && l.unit_price > 0 ? Math.round(l.unit_price * nonUserFactor * 100) / 100
                          : l.unit_price;
                        const displayMonthly = hasBespoke ? (l.tenant_monthly_amount ?? l.tenant_unit_price * (l.qty || 0))
                          : tenantPupm !== null ? tenantPupm * (snap.num_users || 0)
                          : l.monthly_amount > 0 ? Math.round(l.monthly_amount * nonUserFactor * 100) / 100
                          : l.monthly_amount;
                        const qty = l.qty != null ? (Number.isInteger(l.qty) ? l.qty : Number(l.qty).toFixed(2)) : '-';
                        const unitP = l.is_included ? '' : displayUnitPrice > 0 ? `${sym}${Number(displayUnitPrice).toFixed(2)}` : '-';
                        const monthly = l.is_included ? 'Included'
                          : displayMonthly > 0 ? `${sym}${Number(displayMonthly).toFixed(2)}`
                          : l.oneoff_amount > 0 ? `${sym}${Number(l.oneoff_amount).toFixed(2)} (one-off)` : '-';
                        return (
                          <tr key={i} style={{ background: i % 2 === 0 ? 'white' : '#f8fafc', borderBottom: '1px solid hsl(var(--border))', opacity: l.is_included ? 0.7 : 1 }}>
                            <td className="text-center px-4 py-2.5 text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{qty}</td>
                            <td className="px-4 py-2.5">
                              <p className="font-semibold text-sm" style={{ color: l.is_included ? 'hsl(var(--muted-fg))' : 'hsl(var(--c4p-navy-900))' }}>{l.label}</p>
                              {l.sub_label && <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>{l.sub_label}</p>}
                              {l.item_code && <p className="font-mono mt-0.5" style={{ color: '#94a3b8', fontSize: '10px' }}>{l.item_code}</p>}
                              {l.is_included && <span className="text-xs font-semibold px-1.5 py-0.5 rounded" style={{ background: '#dcfce7', color: '#15803d' }}>Included</span>}
                            </td>
                            <td className="text-right px-4 py-2.5 text-xs tabular-nums" style={{ color: 'hsl(var(--muted-fg))' }}>{unitP}</td>
                            <td className="text-right px-4 py-2.5 text-sm font-bold tabular-nums" style={{ color: l.is_included ? 'hsl(var(--muted-fg))' : 'hsl(var(--c4p-navy-900))' }}>{monthly}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: 'hsl(var(--c4p-navy-900))' }}>
                        <td colSpan={3} className="px-4 py-2.5 text-right text-xs font-bold uppercase tracking-wide" style={{ color: 'rgba(255,255,255,0.7)' }}>
                          Monthly Total (ex. {contract.currency === 'GBP' ? 'VAT' : 'tax'}) — {contract.currency}
                        </td>
                        <td className="px-4 py-2.5 text-right font-bold tabular-nums text-sm text-white">
                          {fmt(contract.monthly_total, contract.currency)}
                        </td>
                      </tr>
                      {applyVat && contract.monthly_total > 0 && (
                        <>
                          <tr style={{ borderTop: '1px solid hsl(var(--border))' }}>
                            <td colSpan={3} className="px-4 py-1.5 text-right text-xs font-semibold" style={{ color: '#d97706' }}>VAT @ 20%</td>
                            <td className="px-4 py-1.5 text-right text-xs font-semibold tabular-nums" style={{ color: '#d97706' }}>{sym}{(contract.monthly_total * 0.20).toFixed(2)}</td>
                          </tr>
                          <tr style={{ background: '#fff7ed' }}>
                            <td colSpan={3} className="px-4 py-2 text-right text-xs font-bold">Monthly Total (inc. VAT)</td>
                            <td className="px-4 py-2 text-right text-xs font-bold tabular-nums" style={{ color: '#c2410c' }}>{sym}{(contract.monthly_total * 1.20).toFixed(2)}</td>
                          </tr>
                        </>
                      )}
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ HISTORY TAB ══ */}
        {activeTab === 'history' && (
          <div className="bg-white rounded-xl border overflow-hidden"
            style={{ borderColor: '#e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div className="px-6 py-4 flex items-center justify-between"
              style={{ borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
              <div className="flex items-center gap-2">
                <History size={14} style={{ color: 'hsl(var(--muted-fg))' }} />
                <p className="text-xs font-bold uppercase tracking-widest" style={{ color: 'hsl(var(--muted-fg))' }}>Quote History</p>
              </div>
              <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                style={{ background: '#f1f5f9', color: 'hsl(var(--muted-fg))' }}>
                {(contract.quote_history || []).length + pendingQuotes.length}
              </span>
            </div>
            <div className="p-6 space-y-2">
              {/* Pending Change Requests — not yet signed */}
              {pendingQuotes.length > 0 && (
                <>
                  <p className="text-xs font-semibold uppercase tracking-widest pb-1"
                    style={{ color: 'hsl(var(--muted-fg))' }}>Pending Change Requests</p>
                  {pendingQuotes.map(q => (
                    <PendingCREntry key={q.id} quote={q} />
                  ))}
                  {historyReversed.length > 0 && (
                    <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '12px', marginTop: '8px' }}>
                      <p className="text-xs font-semibold uppercase tracking-widest pb-2"
                        style={{ color: 'hsl(var(--muted-fg))' }}>Signed History</p>
                    </div>
                  )}
                </>
              )}
              {/* Signed quote history */}
              {historyReversed.length === 0 && pendingQuotes.length === 0 ? (
                <p className="text-sm py-4" style={{ color: 'hsl(var(--muted-fg))' }}>No history yet.</p>
              ) : (
                historyReversed.map((entry, idx) => (
                  <HistoryEntry
                    key={entry.quote_id}
                    entry={entry}
                    isFirst={idx === 0}
                    currency={contract.currency}
                  />
                ))
              )}
            </div>
          </div>
        )}

      </div>

      {/* ── Rename Dialog ── */}
      <Dialog open={showRename} onOpenChange={setShowRename}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Rename Contract</DialogTitle>
            <DialogDescription>Enter a new name for this contract.</DialogDescription>
          </DialogHeader>
          <Input
            value={renameName}
            onChange={e => setRenameName(e.target.value)}
            placeholder="Contract name"
            className="mt-2"
            data-testid="contract-rename-input"
            onKeyDown={e => { if (e.key === 'Enter') doRename(renameName.trim()); }}
          />
          <DialogFooter className="mt-2 gap-2">
            <Button variant="outline" onClick={() => setShowRename(false)}>Cancel</Button>
            <Button onClick={() => doRename(renameName.trim())}
              disabled={renaming || !renameName.trim()}
              data-testid="contract-rename-confirm-btn"
              style={{ background: 'hsl(var(--c4p-navy-900))', color: 'white' }}>
              {renaming ? <Loader2 size={13} className="mr-1 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Archive Dialog ── */}
      <Dialog open={showArchive} onOpenChange={setShowArchive}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle size={16} style={{ color: '#d97706' }} />
              Archive Contract?
            </DialogTitle>
            <DialogDescription>This action will archive the contract and remove it from the default Live Contracts view.</DialogDescription>
          </DialogHeader>
          <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
            This will mark <strong>{contract.contract_number}</strong> as archived. The contract and all its
            history will be preserved but it will no longer appear in the default Live Contracts list.
          </p>
          <DialogFooter className="mt-2 gap-2">
            <Button variant="outline" onClick={() => setShowArchive(false)}>Cancel</Button>
            <Button onClick={doArchive} disabled={archiving}
              data-testid="contract-archive-confirm-btn"
              style={{ background: '#b91c1c', color: 'white' }}>
              {archiving ? <Loader2 size={13} className="mr-1 animate-spin" /> : null}
              Archive Contract
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Terminate Dialog ── */}
      <Dialog open={showTerminate} onOpenChange={v => { setShowTerminate(v); if (!v) setTerminateReason(''); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle size={16} style={{ color: '#dc2626' }} />
              Terminate Contract?
            </DialogTitle>
            <DialogDescription>
              This marks the contract as commercially terminated. This is irreversible and distinct from archiving.
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm" style={{ color: '#7f1d1d' }}>
            You are about to terminate <strong>{contract.contract_number}</strong>. This indicates the customer
            has ended the service. The contract will be marked TERMINATED and will no longer appear in the Live Contracts view.
          </p>
          <div className="space-y-1.5 mt-2">
            <label className="text-xs font-semibold" style={{ color: 'hsl(var(--muted-fg))' }}>Reason (optional)</label>
            <Textarea
              value={terminateReason}
              onChange={e => setTerminateReason(e.target.value)}
              placeholder="e.g. Customer ended subscription at contract end date"
              className="h-20 resize-none text-sm"
              data-testid="contract-terminate-reason"
            />
          </div>
          <DialogFooter className="mt-2 gap-2">
            <Button variant="outline" onClick={() => { setShowTerminate(false); setTerminateReason(''); }}>Cancel</Button>
            <Button onClick={doTerminate} disabled={terminating}
              data-testid="contract-terminate-confirm-btn"
              style={{ background: '#dc2626', color: 'white' }}>
              {terminating ? <Loader2 size={13} className="mr-1 animate-spin" /> : <XCircle size={13} className="mr-1" />}
              Terminate Contract
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

